import type { GeographyApi } from "@App/apis/graphql/geography/interfaces";
import { NoLocationFoundError } from "@App/apis/graphql/geography/noLocationFoundError";
import { PickUpAndReturnAddressModal } from "@App/Components/molecules/common/modals/PickUpAndReturnAddressModal";
import { usePremiumServiceObserver } from "@App/hooks/observers/usePremiumServiceObserver";
import { actionCreators, type State } from "@App/state";
import { type Place, ResultType } from "@App/types";
import { PremiumServiceTypes } from "@App/types/enums";
import { bindActionCreators } from "@reduxjs/toolkit";
import { type FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";

export type CollectAndReturnAddressRoutesProps = {
  geographyApi: GeographyApi;
  redirectAfterStore: () => void;
};

/**
 * PickUpAndReturnObservedRoutes renders a modal to get the exact address of the customer. It only displays the modal when a premiumService of type
 * PremiumServiceTypes.COLLECT_AND_RETURN, and there is no complete address in the store.
 *
 * @param geographyApi
 * @param redirectAfterStore The method that gets called after the modal input is submitted.
 * @constructor
 */
export const PickUpAndReturnObservedRoutes: FunctionComponent<
  CollectAndReturnAddressRoutesProps
> = ({ geographyApi, redirectAfterStore }: CollectAndReturnAddressRoutesProps) => {
  const dispatch = useDispatch();
  const {
    removePremiumService,
    choosePlace,
    setStreet,
    setCity,
    setPostcode,
    setAddition,
    setUserConfirmedAddress,
  } = bindActionCreators(actionCreators, dispatch);

  const [addressIsExact, setAddressIsExact] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { t } = useTranslation();

  const [streetValidationMessage, setStreetValidationMessage] = useState(null);
  const [cityValidationMessage, setCityValidationMessage] = useState(null);
  const [postcodeValidationMessage, setPostcodeValidationMessage] = useState(null);

  const [pickUpAndReturnSelected] = usePremiumServiceObserver(
    PremiumServiceTypes.COLLECT_AND_RETURN,
  );

  const premiumServices = useSelector((state: State) => state.orders.premiumServices);
  const place = useSelector((state: State) => state.orders.place);

  const { street, city, postcode, addition, userConfirmedAddress } = useSelector(
    (state: State) => state.orders.address,
  );

  useEffect(() => {
    const addressIsExact = place?.id && place.resultType === ResultType.HOUSE_NUMBER;
    setAddressIsExact(addressIsExact);
  }, [place]);

  const setAddressToState = (place: Place) => {
    place.address?.street &&
      setStreet(`${place.address.street} ${place.address?.houseNumber ?? ""}`);
    place.address?.postalCode && setPostcode(place.address.postalCode);
    place.address?.city && setCity(place.address.city);
  };

  const resetAddressInState = () => {
    setStreet("");
    setPostcode("");
    setCity("");
  };

  useEffect(() => {
    const showModal = pickUpAndReturnSelected && (!addressIsExact || !userConfirmedAddress);
    setShowModal(showModal);
  }, [pickUpAndReturnSelected, addressIsExact, userConfirmedAddress]);

  /**
   * Subscribe to the modal state and on open,
   * set the current place from location search to state so that the form is pre-filled
   */
  useEffect(() => {
    if (showModal) {
      place && setAddressToState(place);
    }
  }, [showModal]);

  const resetValidationMessages = () => {
    setStreetValidationMessage(null);
    setCityValidationMessage(null);
    setPostcodeValidationMessage(null);
  };

  const validate = (): boolean => {
    resetValidationMessages();

    const tempStreetValidationMessage =
      street.length < 1
        ? t("pages.ayvens.pickUpAndReturnObservedRoutes.validation.street.required")
        : null;

    const tempCityValidationMessage =
      city.length < 1
        ? t("pages.ayvens.pickUpAndReturnObservedRoutes.validation.city.required")
        : null;

    const tempPostcodeValidationMessage =
      postcode.length < 4
        ? t("pages.ayvens.pickUpAndReturnObservedRoutes.validation.postcode.required")
        : null;

    setStreetValidationMessage(tempStreetValidationMessage);
    setCityValidationMessage(tempCityValidationMessage);
    setPostcodeValidationMessage(tempPostcodeValidationMessage);

    return (
      !tempStreetValidationMessage && !tempCityValidationMessage && !tempPostcodeValidationMessage
    );
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const location = await geographyApi.getHouseNumberLocation(street, city, postcode);
      choosePlace(location);
      setAddressIsExact(true);
      setUserConfirmedAddress(true);
      setShowModal(false);
      redirectAfterStore();
    } catch (e) {
      if (e instanceof NoLocationFoundError) {
        setError(t("pages.ayvens.pickUpAndReturnObservedRoutes.noLocationFoundError"));
      } else {
        setError(t("pages.ayvens.pickUpAndReturnObservedRoutes.genericError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const cancel = () => {
    const pickUpService = premiumServices.find(
      (premiumService) => premiumService.type === PremiumServiceTypes.COLLECT_AND_RETURN,
    );
    if (pickUpService) removePremiumService(pickUpService);
    // remove the address, that was written to state for pre-filling
    resetAddressInState();
    setShowModal(false);
  };

  const values = {
    street: {
      value: street,
      validationMessage: streetValidationMessage,
      onChange: setStreet,
    },
    city: {
      value: city,
      validationMessage: cityValidationMessage,
      onChange: setCity,
    },
    postcode: {
      value: postcode,
      validationMessage: postcodeValidationMessage,
      onChange: setPostcode,
    },
    addition: {
      value: addition,
      onChange: setAddition,
    },
  };

  return (
    <>
      <PickUpAndReturnAddressModal
        open={showModal}
        loading={loading}
        error={error}
        values={values}
        onSubmit={handleSubmit}
        onCancel={cancel}
      />
      <Outlet />
    </>
  );
};
